import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { firebaseApp } from "../libs/firebase";
import { getSearchApiBaseUrl } from "ui";
import { Box, CircularProgress, Typography, Alert, Button } from "@mui/material";

const ERROR_MESSAGES: Record<string, string> = {
  HANDOFF_NOT_FOUND: "Invalid impersonation link.",
  HANDOFF_ALREADY_USED: "This impersonation link has already been used.",
  HANDOFF_EXPIRED:
    "This impersonation link has expired. Please try again from the admin dashboard.",
};

export function ImpersonatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handoffId = searchParams.get("handoff");
    if (!handoffId) {
      setError("Missing handoff parameter.");
      return;
    }

    const exchange = async () => {
      try {
        const auth = getAuth(firebaseApp);
        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          setError(
            "You must be signed into the creator app before impersonating. Sign in first, then try the link again.",
          );
          return;
        }

        const apiUrl = getSearchApiBaseUrl();
        const resp = await fetch(`${apiUrl}/api/impersonation/exchange`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ handoffId }),
        });

        const data = await resp.json();

        if (!resp.ok) {
          setError(
            ERROR_MESSAGES[data.error] ||
              data.message ||
              `Impersonation failed (${resp.status}).`,
          );
          return;
        }

        // Sign in as the target user with impersonation claims
        await signInWithCustomToken(auth, data.customToken);

        navigate("/home", { replace: true });
      } catch (err) {
        console.error("[ImpersonatePage] exchange error:", err);
        setError("Something went wrong. Please try again.");
      }
    };

    exchange();
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          px: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Button variant="outlined" onClick={() => window.close()}>
          Close Tab
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
      }}
    >
      <CircularProgress />
      <Typography variant="body2" color="text.secondary">
        Setting up impersonation...
      </Typography>
    </Box>
  );
}
